"use client";
import React, { useEffect, useState, Suspense, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
// @ts-ignore
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
// @ts-ignore
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
// @ts-ignore
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
// @ts-ignore
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";

// @ts-ignore
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
import { Environment, OrbitControls, Html, Bounds, Lightformer, Loader, Edges } from "@react-three/drei";
import { Spin } from "antd";
import * as THREE from "three";
import { LoadStep } from "@/lib/utils";
import { EffectComposer, OutlinePass, RenderPass } from "three-stdlib";

// 动态选择合适的加载器
export const chooseLoader = (url: string) => {
	const ext = url.split(".").pop()?.toLowerCase();
	switch (ext) {
		case "gltf":
		case "glb":
			return GLTFLoader;
		case "3dm":
			return Rhino3dmLoader;
		case "obj":
			return OBJLoader;
		case "fbx":
			return FBXLoader;
		case "stl":
			return STLLoader;
		case "stp":
		case "step":
			return "stp";
		default:
			throw new Error(`Unsupported file format: ${url}`);
	}
};

// 缩放视图以适应3D对象并设置中心点
const zoomExtents = (
	object: THREE.Object3D,
	camera: THREE.Camera,
	controls: THREE.EventDispatcher | null,
	zoomed: boolean,
) => {
	if (zoomed) return; // 如果已经缩放过，则跳过
	const box = new THREE.Box3().setFromObject(object);
	const size = box.getSize(new THREE.Vector3());
	const center = box.getCenter(new THREE.Vector3());
	const maxDim = Math.max(size.x, size.y, size.z);
	const cameraZ = 0.85 / maxDim;
	object.scale.set(cameraZ, cameraZ, cameraZ);
	camera.position.set(center.x, center.y, center.y);
	camera.lookAt(center);

	if (controls) {
		// @ts-ignore
		controls?.target.copy(center); // 设置旋转中心为物体的中心
		// @ts-ignore
		controls?.update();
	}
};

// Model 组件：处理 3D 文件的下载、解析和渲染
const Model: React.FC<{
	modalUrl: string;
	fileName: string;
	arrayBuffer?: ArrayBuffer;
	metalness?: number;
	roughness?: number;
	color?: string;
	isHovered: boolean;
}> = ({
	modalUrl,
	fileName,
	arrayBuffer,
	metalness = 0.5,
	roughness = 0.5,
	color = "#cccccc",
	isHovered,
}) => {
	const [object, setObject] = useState<THREE.Object3D | null>(null);
	const [zoomed, setZoomed] = useState(false); // 新增 state 来跟踪是否已进行过缩放
	const { camera, controls } = useThree();

	const loadModel = async () => {
		try {
			let buffer = arrayBuffer;
			if (!buffer) {
				const response = await fetch(modalUrl);
				buffer = await response.arrayBuffer();
			}
			const loaderType = chooseLoader(fileName);
			let loadedObject;
			if (loaderType === "stp") {
				loadedObject = await LoadStep(modalUrl, buffer);
			} else {
				const loader = new loaderType();
				if (loader instanceof Rhino3dmLoader) {
					loader.setLibraryPath("/rhino3dm/");
				} else if (loader instanceof STLLoader) {
					const material = loader.parse(buffer as ArrayBuffer);
					loadedObject = new THREE.Mesh(
						material,
						new THREE.MeshStandardMaterial({
							color,
							metalness,
							roughness,
						}),
					);
				} else {
					loadedObject = await new Promise((resolve, reject) => {
						loader.parse(buffer, "", (object: any) => resolve(object.scene || object), reject);
					});
				}
			}
			setObject(loadedObject);
			zoomExtents(loadedObject, camera, controls, zoomed);
			setZoomed(true); // 标记已缩放
			loadedObject.rotation.x = Math.PI / 2;
		} catch (error) {
			console.error("Error loading model:", error);
		}
	};

	useEffect(() => {
		loadModel();
	}, [modalUrl, arrayBuffer]);

	useEffect(() => {
		if (metalness === 0.5 && roughness === 0.5 && (color === "#cccccc" || color === "#fafafa")) return;
		if (object) {
			object.traverse((child: any) => {
				if (child instanceof THREE.Mesh) {
					const material = child.material as THREE.Material;
					child.material = material.clone();
					child.material.color = new THREE.Color(color);
					child.material.metalness = metalness;
					child.material.roughness = roughness;

					// child.material = new THREE.MeshPhysicalMaterial({
					// 	color,
					// 	metalness,
					// 	roughness,
					// 	side:THREE.DoubleSide
					// });
				}
			});
		}
	}, [metalness, roughness, color, object]);

	// 使用 useFrame 实现自动旋转
	useFrame((state, delta) => {
		if (object) {
			if (!isHovered) {
				object.rotation.z += delta * 0.3; // 控制旋转速度
			}
			const box = new THREE.Box3().setFromObject(object);
			const center = box.getCenter(new THREE.Vector3());
			object.position.sub(center); // 移动对象位置到几何体中心
		}
	});

	return object ? (
		<group>
			{/* 实体模型 */}
			<primitive object={object} dispose={null} />
		</group>
	) : null;
};

// 主要组件：加载和渲染 3D 文件
const LoadNormalModal: React.FC<{
	modalUrl: string;
	className?: string;
	fileName: string;
	arrayBuffer?: ArrayBuffer;
	metalness?: number;
	roughness?: number;
	color?: string;
	setHover?: any;
}> = ({ modalUrl, className, fileName, arrayBuffer, metalness, roughness, color, setHover }) => {
	const [isHovered, setIsHovered] = useState(false);
	const handlerHover = (state) => {
		// setHover(state);
		setIsHovered(state);
	};

	return (
		<Canvas
			shadows
			className={` ${className || ""}`}
			onCreated={({ gl }) => {
				if (typeof window !== "undefined") {
					gl.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
				}
			}}
			onWheel={(e) => e.stopPropagation()}
			onMouseEnter={() => handlerHover(true)}
			onMouseLeave={() => handlerHover(false)}
		>
			{/*<ambientLight intensity={0.5} />*/}
			{/*<directionalLight position={[5, 5, 5]} intensity={1.5} castShadow />*/}

			{/*<BlenderLighting></BlenderLighting>*/}
			{/* 环境光提供整体照明 */}
			<ambientLight intensity={0.4} />

			{/* 主要方向光，动态跟随摄像机 */}
			<FollowCameraLight />
			{/*<ambientLight intensity={Math.PI / 2} />*/}
			{/*<spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} decay={0} intensity={Math.PI} />*/}
			{/*<pointLight position={[-10, -10, -10]} decay={0} intensity={Math.PI} />*/}
			{color !== "#cccccc" && color !== "#fafafa" && (
				<Environment
					preset={null}
					background={false}
					path="/stary3D/"
					files={["st_fagans_interior_1k.hdr"]}
				/>
			)}

			<Suspense
				fallback={
					<Html center>
						{" "}
						<Loader />
					</Html>
				}
			>
				<Bounds fit clip observe={false} margin={0.25} maxDuration={1}>
					<Model
						modalUrl={modalUrl}
						fileName={fileName}
						arrayBuffer={arrayBuffer}
						metalness={metalness}
						roughness={roughness}
						color={color}
						isHovered={isHovered}
					/>
				</Bounds>
			</Suspense>
			<OrbitControls
				makeDefault
				maxPolarAngle={Math.PI}
				enableZoom={true}
				minDistance={0.8}
				maxDistance={2}
			/>
			{/*<axesHelper args={[100]} />*/}
			{/*<gridHelper args={[1000, 100, 0x888888, 0x444444]} />*/}
		</Canvas>
	);
};
// 动态跟随光源组件
const FollowCameraLight = () => {
	const lightRef = useRef<THREE.DirectionalLight>(null);
	const cameraRef = useRef();

	useFrame(({ camera }) => {
		// 使光源始终位于摄像机的前方
		if (lightRef.current) {
			lightRef.current.position.copy(camera.position);
			lightRef.current.target.position.copy(
				new THREE.Vector3(0, 0, 0), // 确保光照始终指向模型中心
			);
			lightRef.current.target.updateMatrixWorld();
		}
	});

	return (
		<directionalLight
			ref={lightRef}
			intensity={0.8} // 光源强度
			color="#ffffff"
			castShadow
			shadow-mapSize-width={1024}
			shadow-mapSize-height={1024}
		/>
	);
};

const BlenderLighting = () => {
	return (
		<>
			{/* 柔和环境光 */}
			<ambientLight intensity={0.3} color="#ffffff" />

			{/* 主方向光：正面光，模拟自然光 */}
			<directionalLight
				position={[5, 5, 5]}
				intensity={0.8} // 中等强度
				color="#ffffff"
				castShadow
				shadow-mapSize-width={1024}
				shadow-mapSize-height={1024}
			/>

			{/* 侧面补光：增加物体立体感 */}
			<directionalLight position={[-5, 2, 5]} intensity={0.5} color="#dddddd" />

			{/* 背面补光：模拟反射光，勾勒轮廓 */}
			<directionalLight position={[0, -3, -5]} intensity={0.4} color="#bbbbbb" />

			{/* 两侧点光源，柔和边缘 */}
			<pointLight position={[10, 5, 0]} intensity={0.3} color="#ffffff" />
			<pointLight position={[-10, -5, 0]} intensity={0.3} color="#ffffff" />
		</>
	);
};

export default LoadNormalModal;
